/**
 * Compound circle collision shapes for ships.
 *
 * Each ship class has an array of circles in LOCAL space (ship center = origin, +Y = forward/nose).
 * The circles are rotated by the ship's heading at runtime.
 *
 * Why compound circles instead of a single circle:
 * - A Corvette (33×44 px) and a Dreadnought (640×840 px) can't share the same radius
 * - Ship silhouettes are elongated/irregular — a single circle wastes too much empty space
 * - Compound circles are cheap (2-4 per ship) and preserve our existing Newton's impulse physics
 *
 * Coordinate convention: x=0 is center of ship, oy<0 is FORWARD (nose/up on screen), oy>0 is AFT (engines/down)
 */

export interface CollisionCircle {
  /** Offset from ship center along ship's lateral axis (perpendicular to heading) */
  ox: number;
  /** Offset from ship center along ship's forward axis (heading direction) */
  oy: number;
  /** Radius of this collision circle */
  r: number;
}

export type ShipCollisionShape = CollisionCircle[];

/**
 * Collision shapes per ship CLASS (not per individual ship — all corvettes share the same shape).
 * SIZE multiplier is applied at runtime (small=0.85, medium=1.0, large=1.25) matching visual scale.
 *
 * These are tuned against the SVG assets rendered at base scale:
 * - SVG dimensions × meta.scale = pixel size in game
 * - Circles should cover the silhouette snugly, not generously
 *
 * Note: After SVG rotation (-90°), the original SVG width becomes the ship's height (nose-to-tail)
 * and the original SVG height becomes the ship's width (wing-to-wing).
 * So in-game: height = svgWidth * scale, width = svgHeight * scale
 */
export const SHIP_COLLISION_SHAPES: Record<string, ShipCollisionShape> = {
  // Corvette: 166×218 @ 0.20 → ~33h × 44w in-game. Tiny dart shape.
  // Single circle is fine — it's so small the difference is negligible.
  corvette: [
    { ox: 0, oy: 0, r: 16 },
  ],

  // Frigate: 180×240 @ 0.35 → ~63h × 84w. Compact with swept wings.
  // Two circles: nose + wing area
  // oy < 0 = nose (up), oy > 0 = aft (down)
  frigate: [
    { ox: 0, oy: -8, r: 18 },   // nose/cockpit
    { ox: 0, oy: 12, r: 24 },   // wing span area (aft)
  ],

  // Destroyer: 196×164 @ 0.50 → ~98h × 82w. Wide, flat, angular.
  // Two circles stacked: front armor plate + rear engines
  destroyer: [
    { ox: 0, oy: -16, r: 28 },   // front section (nose)
    { ox: 0, oy: 18, r: 26 },    // rear section (engines)
  ],

  // Cruiser: 220×300 @ 0.55 → ~121h × 165w. Long, elegant.
  // Three circles: nose, midsection, engines
  cruiser: [
    { ox: 0, oy: -30, r: 24 },   // nose
    { ox: 0, oy: 0, r: 32 },     // mid body
    { ox: 0, oy: 30, r: 28 },    // engines
  ],

  // Hauler: 160×200 @ 0.30 → ~48h × 60w. Boxy, compact.
  // Two circles: cockpit + cargo bay
  hauler: [
    { ox: 0, oy: -6, r: 16 },    // cockpit (nose)
    { ox: 0, oy: 10, r: 18 },    // cargo bay (aft)
  ],

  // Battleship: SVG 275×222 @ 1.10 → game 244w × 302h (half: ±122w, ±151h).
  // oy < 0 = NOSE (up), oy > 0 = AFT/engines (down).
  battleship: [
    { ox:   0, oy: -120, r: 40 },  // prow tip
    { ox: -55, oy:  -70, r: 55 },  // left shoulder
    { ox:  55, oy:  -70, r: 55 },  // right shoulder
    { ox: -70, oy:   20, r: 60 },  // left broadside (widest)
    { ox:  70, oy:   20, r: 60 },  // right broadside
    { ox: -50, oy:  100, r: 50 },  // left engine flare
    { ox:  50, oy:  100, r: 50 },  // right engine flare
  ],

  // Carrier: SVG 251×253 @ 1.40 → game 354w × 351h (half: ±177w, ±176h).
  // Wide swept wings forming V up top, central ring body, lower hull + tail.
  // oy < 0 = NOSE/wings (up), oy > 0 = AFT/tail (down).
  carrier: [
    { ox: -120, oy: -130, r: 55 },  // left wing tip
    { ox:  120, oy: -130, r: 55 },  // right wing tip
    { ox:  -75, oy:  -60, r: 55 },  // left wing root
    { ox:   75, oy:  -60, r: 55 },  // right wing root
    { ox:    0, oy:  -10, r: 50 },  // central ring
    { ox:  -80, oy:   50, r: 55 },  // left lower hull
    { ox:   80, oy:   50, r: 55 },  // right lower hull
    { ox:    0, oy:  110, r: 55 },  // tail / engine block
  ],

  // Dreadnought: SVG 285×196 @ 2.00 → game 392w × 570h (half: ±196w, ±285h).
  // Values in game-space (SVG coords × scale 2.0). Bilateral structure.
  // oy < 0 = NOSE (up), oy > 0 = AFT (down).
  dreadnought: [
    { ox:  -90, oy: -220, r: 65 },  // left forward wing
    { ox:   90, oy: -220, r: 65 },  // right forward wing
    { ox:  -50, oy: -110, r: 75 },  // left forward hull
    { ox:   50, oy: -110, r: 75 },  // right forward hull
    { ox:    0, oy:    0, r: 65 },  // central bridge
    { ox:  -50, oy:  120, r: 75 },  // left aft hull
    { ox:   50, oy:  120, r: 75 },  // right aft hull
    { ox: -110, oy:  215, r: 65 },  // left aft wing
    { ox:  110, oy:  215, r: 65 },  // right aft wing
  ],
};

/**
 * Size multipliers matching visual rendering (from ShipGraphics SIZE_SCALE_MULTIPLIERS).
 * Applied to both circle offsets and radii.
 */
export const COLLISION_SIZE_MULTIPLIERS: Record<string, number> = {
  small: 0.85,
  medium: 1.0,
  large: 1.25,
};

/**
 * Get the collision shapes for a ship, scaled by its size category.
 */
export function getShipCollisionShapes(shipClass: string, size: string): ShipCollisionShape {
  const base = SHIP_COLLISION_SHAPES[shipClass];
  if (!base) {
    // Fallback: single circle
    return [{ ox: 0, oy: 0, r: 25 }];
  }
  const mul = COLLISION_SIZE_MULTIPLIERS[size] || 1.0;
  if (mul === 1.0) return base;
  return base.map(c => ({
    ox: c.ox * mul,
    oy: c.oy * mul,
    r: c.r * mul,
  }));
}

/**
 * Get the bounding radius for a ship's compound shape (for broad-phase spatial hash queries).
 * This is the maximum distance from center that any circle extends to.
 */
export function getShipBoundingRadius(shipClass: string, size: string): number {
  const shapes = getShipCollisionShapes(shipClass, size);
  let maxR = 0;
  for (const c of shapes) {
    const extent = Math.sqrt(c.ox * c.ox + c.oy * c.oy) + c.r;
    if (extent > maxR) maxR = extent;
  }
  return maxR;
}

/**
 * Transform a collision circle from local ship space to world space.
 * heading is in radians (0 = right/east, but ships point UP so heading=0 means north).
 */
export function transformCircleToWorld(
  circle: CollisionCircle,
  shipX: number,
  shipY: number,
  heading: number
): { x: number; y: number; r: number } {
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  // Rotate local offset by heading
  // In our coord system: oy is forward (along heading), ox is lateral (perpendicular)
  // Forward direction vector: (sin(heading), -cos(heading)) for screen coords where Y-down
  // Lateral direction vector: (cos(heading), sin(heading))
  return {
    x: shipX + circle.ox * cos - circle.oy * sin,
    y: shipY + circle.ox * sin + circle.oy * cos,
    r: circle.r,
  };
}

/**
 * Check if any circle of shape A overlaps any circle of shape B (both in world space).
 * Returns the pair with deepest penetration, or null if no overlap.
 */
export function checkCompoundOverlap(
  shapesA: { x: number; y: number; r: number }[],
  shapesB: { x: number; y: number; r: number }[]
): { circleA: { x: number; y: number; r: number }; circleB: { x: number; y: number; r: number }; penetration: number } | null {
  let best: { circleA: { x: number; y: number; r: number }; circleB: { x: number; y: number; r: number }; penetration: number } | null = null;

  for (const a of shapesA) {
    for (const b of shapesB) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distSq = dx * dx + dy * dy;
      const minDist = a.r + b.r;
      if (distSq <= minDist * minDist) {
        const dist = Math.sqrt(distSq);
        const penetration = minDist - dist;
        if (!best || penetration > best.penetration) {
          best = { circleA: a, circleB: b, penetration };
        }
      }
    }
  }
  return best;
}

/**
 * Check if a point (projectile) hits any circle in a compound shape.
 * Returns true if the point is within any circle + projectileRadius.
 */
export function pointHitsCompoundShape(
  px: number, py: number, projectileRadius: number,
  worldCircles: { x: number; y: number; r: number }[]
): boolean {
  for (const c of worldCircles) {
    const dx = px - c.x;
    const dy = py - c.y;
    const distSq = dx * dx + dy * dy;
    const hitDist = c.r + projectileRadius;
    if (distSq <= hitDist * hitDist) return true;
  }
  return false;
}
